import {doneTaskKeys, tasksToCelebrate} from './learnModel';

/**
 * Lo que «Aprende Dashbo» recuerda en el navegador, siempre por (usuario, agencia) — contrato §6:
 * un superadmin se mueve entre agencias y una persona puede cambiar de agencia, y la memoria del
 * drawer viejo era sólo por agencia.
 *
 * - **Lo ya celebrado**: las tareas que ya se festejaron. Sobrevive a la recarga a propósito: la
 *   primera consulta se hace en ChatGPT o Claude, y quien vuelve mañana tiene que ver su «¡Listo!».
 *   La primera vez (sin memoria) se siembra con lo que ya estaba hecho, sin celebrar nada.
 * - **Si la página está habilitada**: para decidir al primer pintado entre el lanzador nuevo y el
 *   drawer viejo sin esperar el pedido (el marco se monta de nuevo en cada navegación).
 * - **Cuándo se vio lo esencial completo**: la semana del lanzador, si el backend no trae fechas.
 * - **Qué guías ya se lanzaron desde la página**: el botón de la tarea abre la guía sólo la primera vez.
 *
 * `localStorage` puede no estar (incógnito, bloqueado): todo va con try/catch y cae a una memoria de
 * la pestaña, que alcanza para que nada se celebre dos veces mientras dure.
 */

const CELEBRATED_PREFIX = 'dashbo_learn_celebrated_';
const ENABLED_PREFIX = 'dashbo_learn_enabled_';
const ESSENTIALS_DONE_PREFIX = 'dashbo_learn_essentials_done_';
const GUIDES_SEEN_PREFIX = 'dashbo_learn_guides_seen_';

const tabMemory = new Map();

/**
 * La clave de la persona en esta agencia, o `null` sin sesión. También sin agencia: justo después del
 * login `logged_agency_id` todavía no está (lo escribe `get-logged-user-data/`), y una clave «sin
 * agencia» la compartirían todas las agencias de esa persona (mismo criterio que `LocalStorage`).
 */
export function ownerKey(userId, agencyId) {
    if (!userId || !agencyId) return null;
    return `u${userId}_a${agencyId}`;
}

function read(key) {
    try {
        const value = window.localStorage.getItem(key);
        if (value !== null) return value;
    } catch (e) {
        // Sin storage: queda la memoria de la pestaña.
    }
    return tabMemory.has(key) ? tabMemory.get(key) : null;
}

function write(key, value) {
    tabMemory.set(key, value);
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Idem.
    }
}

function readCelebrated(owner) {
    const raw = read(CELEBRATED_PREFIX + owner);
    if (raw === null) return null;
    try {
        const keys = JSON.parse(raw);
        return new Set(Array.isArray(keys) ? keys : []);
    } catch (e) {
        return null;
    }
}

/**
 * Las tareas para celebrar ahora, y anotarlas. Se anotan TODAS las hechas (unión, nunca se borra):
 * desmarcar y volver a marcar a mano no repite la fiesta, y la primera vez siembra sin celebrar.
 */
export function takeCelebrations(owner, model) {
    if (!owner || !model) return [];
    const celebrated = readCelebrated(owner);
    const fresh = tasksToCelebrate(celebrated, model);
    const all = new Set([...(celebrated || []), ...doneTaskKeys(model)]);
    write(CELEBRATED_PREFIX + owner, JSON.stringify([...all]));
    return fresh;
}

/** `true` / `false` si ya se supo alguna vez para esta persona, `null` si no. */
export function rememberedEnabled(owner) {
    if (!owner) return null;
    const value = read(ENABLED_PREFIX + owner);
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
}

export function rememberEnabled(owner, enabled) {
    if (owner) write(ENABLED_PREFIX + owner, enabled ? '1' : '0');
}

/** Cuándo vio este navegador lo esencial completo por primera vez (ISO), anotándolo si es ahora. */
export function essentialsFirstSeenDone(owner, now = new Date()) {
    if (!owner) return null;
    const key = ESSENTIALS_DONE_PREFIX + owner;
    const value = read(key);
    if (value) return value;
    const stamp = now.toISOString();
    write(key, stamp);
    return stamp;
}

/**
 * Las tareas cuya guía ya se lanzó desde la página (C-04): el botón único de la tarea abre la guía la
 * primera vez y después sólo lleva a la pantalla. Por persona y agencia, como lo demás.
 */
export function guideSeen(owner, taskKey) {
    if (!owner || !taskKey) return false;
    try {
        const keys = JSON.parse(read(GUIDES_SEEN_PREFIX + owner) || '[]');
        return Array.isArray(keys) && keys.includes(taskKey);
    } catch (e) {
        return false;
    }
}

export function rememberGuideSeen(owner, taskKey) {
    if (!owner || !taskKey || guideSeen(owner, taskKey)) return;
    let keys = [];
    try {
        const parsed = JSON.parse(read(GUIDES_SEEN_PREFIX + owner) || '[]');
        keys = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        keys = [];
    }
    write(GUIDES_SEEN_PREFIX + owner, JSON.stringify([...keys, taskKey]));
}

/** Sólo para los tests: la memoria de la pestaña sobrevive entre casos. */
export function resetLearnMemory() {
    tabMemory.clear();
}
